/**
 * shadcn Combobox 조합 API를 Radix Popover + RDS Input/Dropdown에 맞춘 구현.
 * 공식 컴포넌트는 Base UI라 이 프로젝트(Radix)에는 넣지 않는다.
 * @see https://ui.shadcn.com/docs/components/combobox
 */
import { Input } from '@/shared/components/ui/input';
import {
  dropdownMenuContentClassName,
  dropdownMenuItemVariants,
} from '@/shared/components/ui/dropdown-menu';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/utils';
import * as React from 'react';

interface ComboboxContextValue<T> {
  items: T[];
  itemToStringValue: (item: T) => string;
  selected: T | null;
  onSelectItem: (item: T) => void;
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  /** 방향키로 하이라이트를 옮긴다. 닫혀 있으면 열면서 현재 선택값 기준으로 옮긴다 */
  moveHighlight: (delta: 1 | -1) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  listId: string;
  getOptionId: (index: number) => string;
}

const ComboboxContext = React.createContext<ComboboxContextValue<unknown> | null>(
  null,
);

const useComboboxContext = <T,>() => {
  const ctx = React.useContext(ComboboxContext);
  if (!ctx) {
    throw new Error('Combobox 하위 컴포넌트는 Combobox 안에서만 사용할 수 있습니다.');
  }
  return ctx as ComboboxContextValue<T>;
};

export interface ComboboxProps<T> {
  items: T[];
  value?: T | null;
  onValueChange?: (item: T | null) => void;
  itemToStringValue: (item: T) => string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  /**
   * 입력창이 Popover 밖에 있으므로 기본 false.
   * true면 리스트가 열린 동안 입력이 pointer-events:none에 막힌다.
   */
  modal?: boolean;
  /**
   * 방향키로 하이라이트를 옮길 때마다 호출한다.
   * 네이티브 select처럼 위아래 키로 바로 값을 바꾸고 싶을 때 쓴다.
   */
  onHighlightChange?: (item: T) => void;
}

export const Combobox = <T,>({
  items,
  value = null,
  onValueChange,
  itemToStringValue,
  open: openProp,
  onOpenChange,
  children,
  modal = false,
  onHighlightChange,
}: ComboboxProps<T>) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const listId = React.useId();

  // 방향키로 연 경우에는 키 입력이 정한 하이라이트를 유지한다
  const openedByKeyboardRef = React.useRef(false);

  const selectedIndex =
    value == null
      ? -1
      : items.findIndex(
          (item) => itemToStringValue(item) === itemToStringValue(value),
        );

  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [items]);

  // 목록을 열면 현재 선택값을 하이라이트한다 (드롭다운처럼 지금 값에서 위아래로 움직이게)
  React.useEffect(() => {
    if (!open) return;
    if (openedByKeyboardRef.current) {
      openedByKeyboardRef.current = false;
      return;
    }
    if (selectedIndex >= 0) setHighlightedIndex(selectedIndex);
  }, [open, selectedIndex]);

  const moveHighlight = (delta: 1 | -1) => {
    if (items.length === 0) {
      setOpen(true);
      return;
    }
    let base = highlightedIndex;
    if (!open) {
      openedByKeyboardRef.current = true;
      setOpen(true);
      base = selectedIndex >= 0 ? selectedIndex : delta > 0 ? -1 : 0;
    }
    const next = (base + delta + items.length) % items.length;
    setHighlightedIndex(next);
    onHighlightChange?.(items[next]);
  };

  const onSelectItem = (item: T) => {
    onValueChange?.(item);
    setOpen(false);
  };

  return (
    <ComboboxContext.Provider
      value={{
        items: items as unknown[],
        itemToStringValue: itemToStringValue as (item: unknown) => string,
        selected: value as unknown,
        onSelectItem: onSelectItem as (item: unknown) => void,
        highlightedIndex,
        setHighlightedIndex,
        moveHighlight,
        open,
        setOpen,
        listId,
        getOptionId: (index: number) => `${listId}-option-${index}`,
      }}
    >
      <Popover modal={modal} open={open} onOpenChange={setOpen}>
        <div className="w-full">{children}</div>
      </Popover>
    </ComboboxContext.Provider>
  );
};

export type ComboboxInputProps = Omit<
  React.ComponentProps<typeof Input>,
  'onChange' | 'value'
> & {
  value?: string;
  onValueChange?: (value: string) => void;
  /** 하이라이트 항목이 없을 때 Enter — 서버 검색 등 */
  onSearch?: (value: string) => void;
};

export const ComboboxInput = React.forwardRef<
  HTMLInputElement,
  ComboboxInputProps
>(
  (
    {
      className,
      value = '',
      onValueChange,
      onSearch,
      onFocus,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const {
      items,
      highlightedIndex,
      moveHighlight,
      open,
      setOpen,
      listId,
      getOptionId,
      onSelectItem,
    } = useComboboxContext();

    return (
      <PopoverAnchor asChild>
        <div className="w-full">
          <Input
            ref={ref}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && items[highlightedIndex]
                ? getOptionId(highlightedIndex)
                : undefined
            }
            autoComplete="off"
            value={value}
            className={cn('w-full', className)}
            onChange={(event) => {
              onValueChange?.(event.target.value);
              if (items.length > 0) setOpen(true);
            }}
            onFocus={(event) => {
              if (items.length > 0) setOpen(true);
              onFocus?.(event);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
                return;
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                const highlighted = items[highlightedIndex];
                if (open && highlighted) {
                  onSelectItem(highlighted);
                  return;
                }
                onSearch?.(value);
                return;
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
                return;
              }
              onKeyDown?.(event);
            }}
            {...props}
          />
        </div>
      </PopoverAnchor>
    );
  },
);
ComboboxInput.displayName = 'ComboboxInput';

export const ComboboxContent = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof PopoverContent>) => (
  <PopoverContent
    align="start"
    sideOffset={8}
    onOpenAutoFocus={(event) => event.preventDefault()}
    className={cn(
      dropdownMenuContentClassName,
      // Dialog는 body에 pointer-events:none을 걸어서, 포털된 리스트는 다시 켜 줘야 한다
      'z-[100] w-[var(--radix-popper-anchor-width)] p-0 py-2 pointer-events-auto',
      className,
    )}
    {...props}
  >
    {children}
  </PopoverContent>
);

export const ComboboxEmpty = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { items } = useComboboxContext();
  if (items.length > 0) return null;
  return (
    <div
      className={cn(
        'px-4 py-6 text-center text-sm text-ruda-text-tertiary',
        className,
      )}
      {...props}
    />
  );
};

export interface ComboboxListProps<T> {
  children: (item: T, index: number) => React.ReactNode;
  className?: string;
}

export const ComboboxList = <T,>({
  children,
  className,
}: ComboboxListProps<T>) => {
  const { items, listId } = useComboboxContext<T>();
  if (items.length === 0) return null;
  return (
    <div
      id={listId}
      role="listbox"
      className={cn('max-h-60 overflow-y-auto', className)}
    >
      {items.map((item, index) => children(item, index))}
    </div>
  );
};

export interface ComboboxItemProps<T>
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: T;
}

export const ComboboxItem = <T,>({
  value,
  className,
  children,
  ...props
}: ComboboxItemProps<T>) => {
  const {
    items,
    itemToStringValue,
    selected,
    highlightedIndex,
    setHighlightedIndex,
    getOptionId,
    onSelectItem,
  } = useComboboxContext<T>();
  const index = items.findIndex(
    (item) => itemToStringValue(item) === itemToStringValue(value),
  );
  const isHighlighted = index === highlightedIndex;
  const isSelected =
    selected != null &&
    itemToStringValue(selected) === itemToStringValue(value);
  const itemRef = React.useRef<HTMLButtonElement>(null);

  // 방향키로 옮긴 하이라이트가 목록 밖으로 나가지 않게 따라 스크롤한다
  React.useEffect(() => {
    if (isHighlighted) itemRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [isHighlighted]);

  return (
    <button
      ref={itemRef}
      type="button"
      role="option"
      id={index >= 0 ? getOptionId(index) : undefined}
      aria-selected={isSelected}
      data-highlighted={isHighlighted || undefined}
      className={cn(
        dropdownMenuItemVariants({ selected: isSelected }),
        className,
      )}
      {...props}
      onMouseEnter={(event) => {
        if (index >= 0) setHighlightedIndex(index);
        props.onMouseEnter?.(event);
      }}
      // Dialog의 onPointerDownOutside preventDefault가 click을 취소한다.
      // 포털된 옵션은 pointerdown에서 골라야 선택이 된다.
      onPointerDown={(event) => {
        event.preventDefault();
        props.onPointerDown?.(event);
        onSelectItem(value);
      }}
    >
      {children}
    </button>
  );
};
